import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';

/**
 * Shown when a sign-in attempt is refused because the user's email address is not verified.
 *
 * This replaces the generic "Error Message" modal for this one case, for two reasons.
 *
 * Tone. A user who has simply not clicked a link in an email has not done anything wrong, and the
 * shared modal has no way to say so: its title is the hardcoded string "Error Message" and its only
 * button is "Close". That framing tells the user something has broken, when the truth is that there is
 * one small step left and the product knows exactly what it is.
 *
 * Capability. The shared modal renders its body with [innerHTML], so no Angular event binding can be
 * placed inside it, which makes a working "resend" button impossible there. A real dialog component can
 * own the action, its in-flight state and its result.
 *
 * The email address is supplied by the caller: either what the user typed into the sign-in form, or the
 * address the API returned on the 403 (which is the only source available on the auto sign-in path,
 * where the user never typed anything).
 */
// No styleUrls: the dialog header, separators and action row come entirely from the shared
// .dialog-title / .dialog-close-button rules in styles.scss and Bootstrap spacing utilities, which is
// what keeps it looking like every other dialog in the portal.
@Component({
  selector: 'app-verify-email-required-dialog',
  templateUrl: './verify-email-required-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VerifyEmailRequiredDialogComponent {
  /** The address the verification link will be sent to. May be blank on unusual API responses. */
  email: string = '';

  /** The API's explanation of why sign-in was refused. */
  message: string = '';

  /** True once a resend has been requested, which swaps the action out for a confirmation. */
  resendSent: boolean = false;

  /** True while the resend request is in flight, to stop a double submission. */
  inProgress: boolean = false;

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService,
    private dialogRef: MatDialogRef<VerifyEmailRequiredDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.email = (this.data && this.data.email) || '';
    this.message =
      (this.data && this.data.message) ||
      'Please verify your email address to continue.';
  }

  /**
   * Request a fresh verification link.
   *
   * The endpoint answers identically whether or not it sent anything, by design: it must not reveal
   * which addresses exist or still need verification. So there is no success or failure to report back
   * beyond the generic acknowledgement, and the button is swapped for that acknowledgement either way.
   */
  onResend(): void {
    if (this.inProgress || this.resendSent) {
      return;
    }

    if (!this.email) {
      this.utils.showToast(
        'We could not tell which email address to send to. Please enter your email address and try signing in again.'
      );
      return;
    }

    this.inProgress = true;

    this.api
      .makeRequest('post', 'v2/verify-email/resend', { email: this.email })
      .then((): void => {
        this.inProgress = false;
        this.resendSent = true;
      })
      .catch((errorResponse): void => {
        this.inProgress = false;

        // The route allows 6 requests a minute per caller. Somebody clicking repeatedly should be told
        // to wait, not shown a raw throttling error.
        if (errorResponse && errorResponse.status === 429) {
          this.utils.showToast(
            'You have requested this a few times already. Please wait a minute before trying again.'
          );
          return;
        }

        this.utils.showToast(
          'We could not send the verification email just now. Please try again in a few minutes.'
        );
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
